import axios from 'axios';
import { looksLikeMaintenance, signalMaintenance } from './maintenance';

const API_BASE_URL = process.env.REACT_APP_API_URL || '/api';

const api = axios.create({
    baseURL: API_BASE_URL,
    headers: {
        'Content-Type': 'application/json',
    },
});

// Add token to requests if available
api.interceptors.request.use(
    (config) => {
        const token = localStorage.getItem('access_token');
        if (token) {
            config.headers.Authorization = `Bearer ${token}`;
        }
        return config;
    },
    (error) => {
        return Promise.reject(error);
    }
);

// Handle token expiration — only redirect if user was already logged in.
// Also spot a deploy in progress: nginx then answers every /api call with the maintenance web page
// (status 200) or not at all. An already-open page would choke on that and go white, so switch
// to the "being updated" screen instead (see maintenance.js).
api.interceptors.response.use(
    (response) => {
        if (looksLikeMaintenance({ response })) {
            signalMaintenance();
            return Promise.reject(new Error('Vendora is being updated'));
        }
        return response;
    },
    (error) => {
        if (!axios.isCancel(error) && looksLikeMaintenance({ error })) {
            signalMaintenance();
        }
        if (error.response && error.response.status === 401) {
            const hadToken = !!localStorage.getItem('access_token');
            localStorage.removeItem('access_token');
            localStorage.removeItem('user_id');
            localStorage.removeItem('mobile');
            if (hadToken) {
                window.location.href = '/';
            }
        }
        return Promise.reject(error);
    }
);

export const authAPI = {
    checkMobile: (mobile) => api.post('/auth/check-mobile', { mobile }),
    sendOTP: (mobile, channel) => api.post('/auth/send-otp', { mobile, channel }),
    verifyOTPAndSetPassword: (mobile, otp, password, name, businessId = null) =>
        api.post('/auth/verify-otp-and-set-password',
            { mobile, otp },
            { params: { password, ...(name ? { name } : {}), ...(businessId ? { business_id: businessId } : {}) } }
        ),
    loginWithPassword: (mobile, password, deviceInfo) => 
        api.post('/auth/login-with-password', { mobile, password, device_info: deviceInfo }),
    logout: () => api.post('/auth/logout'),
    getCurrentUser: () => api.get('/auth/me'),
    getSessions: () => api.get('/auth/sessions'),
    updateProfile: (profileData) => api.put('/auth/profile', profileData),
};

export const businessAPI = {
    createBusiness: (businessData) => api.post('/business/create', businessData),
    listBusinesses: () => api.get('/business/list'),
    listCustomerVendors: () => api.get('/business/customers/me/vendors'),
    getMyActivity: (limit = 30) => api.get('/business/customers/me/activity', { params: { limit } }),
    // Registered customer confirms a payment made outside Vendora (UPI).
    // data: { f_reference_type: 'dues'|'bill'|'order', f_reference_id?, f_amount, f_payment_method?, f_message? }
    createPaymentClaim: (businessId, data) => api.post(`/business/${businessId}/my/payment-claim`, data),
    getBusiness: (businessId) => api.get(`/business/${businessId}`),
    updateBusiness: (businessId, businessData) => api.put(`/business/${businessId}`, businessData),
    deleteBusiness: (businessId) => api.delete(`/business/${businessId}`),
    
    // Category management
    listProductCategories: () => api.get('/business/product-categories/list'),
    getBusinessCategories: (businessId) => api.get(`/business/${businessId}/categories`),
    addCategoryToBusiness: (data) => api.post('/business/product-categories/add', data),
    deleteBusinessCategory: (categoryId) => api.delete(`/business/product-categories/${categoryId}`),
    
    // Product management
    listProducts: (categoryId) => api.get('/business/products/list', { params: categoryId ? { category_id: categoryId } : {} }),
    // includePlans adds scheduled plans ("Sunday Curd") to the list. Screens that assign or name
    // what a customer receives need them; the product catalogue and price screens do not.
    getBusinessProducts: (businessId, includePlans = false, includeArchived = false) =>
        api.get(`/business/${businessId}/products`, { params: {
            ...(includePlans ? { include_plans: true } : {}),
            ...(includeArchived ? { include_archived: true } : {}),
        } }),
    // Settings → Deleted
    getDeletedProductPlanHolders: (productId) => api.get(`/business/products/${productId}/customers-on-plan`),
    restoreProduct: (productId) => api.post(`/business/products/${productId}/restore`),
    restoreCustomer: (customerId) => api.post(`/business/customers/${customerId}/restore`),
    // Owner only, for customers already in Deleted. Cannot be undone.
    getDeleteForeverPreview: (customerId) => api.get(`/business/customers/${customerId}/delete-forever-preview`),
    deleteCustomerForever: (customerId) => api.delete(`/business/customers/${customerId}/forever`, { params: { confirm: 'DELETE' } }),
    addProductToBusiness: (data) => api.post('/business/products/add', data),
    updateBusinessProduct: (productId, data) => api.put(`/business/products/${productId}`, data),
    deleteBusinessProduct: (productId) => api.delete(`/business/products/${productId}`),
    // Share products with another vendor: find them by mobile, send, then the receiver
    // opens the share, picks products and copies them in.
    findVendorToShare: (businessId, mobile) => api.post(`/business/${businessId}/share-products/find`, { mobile }),
    shareProducts: (businessId, toBusinessId) => api.post(`/business/${businessId}/share-products`, { to_business_id: toBusinessId }),
    getSharedProducts: (businessId, notificationId) => api.get(`/business/${businessId}/shared-products/${notificationId}`),
    importSharedProducts: (businessId, notificationId, items) => api.post(`/business/${businessId}/shared-products/${notificationId}/import`, { items }),
    // Ends every running plan on the product today; deleteToo also deletes it in the same step.
    endAllPlansOnProduct: (productId, deleteToo) => api.post(`/business/products/${productId}/end-all-plans`, null, { params: { delete: deleteToo } }),
    bulkAddProducts: (data) => api.post('/business/products/bulk-add', data),
    reorderProducts: (businessId, orderedIds) => api.put(`/business/${businessId}/products/reorder`, { ordered_ids: orderedIds }),

    // Plans — a product plus the days it is delivered ("Sunday Curd"). Price comes from the
    // parent product, so there is nothing to price here.
    getPlans: (businessId, includeArchived = false) =>
        api.get(`/business/${businessId}/plans`, { params: { include_archived: includeArchived } }),
    createPlan: (businessId, data) => api.post(`/business/${businessId}/plans`, data),
    updatePlan: (businessId, planId, data) => api.put(`/business/${businessId}/plans/${planId}`, data),
    deletePlan: (businessId, planId) => api.delete(`/business/${businessId}/plans/${planId}`),
    getCategoryOrder: (businessId) => api.get(`/business/${businessId}/category-order`),
    saveCategoryOrder: (businessId, orderedNames) => api.put(`/business/${businessId}/category-order`, { ordered_names: orderedNames }),
    uploadProductImage: (productId, file) => {
        const form = new FormData();
        form.append('file', file);
        return api.post(`/business/products/${productId}/image`, form, { headers: { 'Content-Type': 'multipart/form-data' } });
    },
    deleteProductImage: (productId) => api.delete(`/business/products/${productId}/image`),
    uploadBusinessLogo: (businessId, file) => {
        const form = new FormData();
        form.append('file', file);
        return api.post(`/business/${businessId}/logo`, form, { headers: { 'Content-Type': 'multipart/form-data' } });
    },
    deleteBusinessLogo: (businessId) => api.delete(`/business/${businessId}/logo`),
    getOrderSettings: (businessId) => api.get(`/business/${businessId}/order-settings`),
    updateOrderSettings: (businessId, data) => api.put(`/business/${businessId}/order-settings`, data),
    
    // Price history
    bulkUpdatePrices: (businessId, data) => api.post(`/business/${businessId}/products/bulk-price`, data),
    getPriceHistory: (productId) => api.get(`/business/products/${productId}/price-history`),
    addPriceLog: (productId, data) => api.post(`/business/products/${productId}/price-history`, data),
    deletePriceLog: (productId, priceLogId) => api.delete(`/business/products/${productId}/price-history/${priceLogId}`),
    
    // Discount history
    getDiscountHistory: (productId) => api.get(`/business/products/${productId}/discount-history`),
    addDiscountLog: (productId, data) => api.post(`/business/products/${productId}/discount-history`, data),
    updateDiscountLog: (productId, discountLogId, data) => api.put(`/business/products/${productId}/discount-history/${discountLogId}`, data),
    deleteDiscountLog: (productId, discountLogId) => api.delete(`/business/products/${productId}/discount-history/${discountLogId}`),
    toggleDiscountStatus: (productId, discountLogId) => api.patch(`/business/products/${productId}/discount-history/${discountLogId}/toggle`),
    
    // Area management
    getBusinessAreas: (businessId) => api.get(`/business/${businessId}/areas`),
    createBusinessArea: (data) => api.post('/business/areas', data),
    updateBusinessArea: (areaId, data) => api.put(`/business/areas/${areaId}`, data),
    deleteBusinessArea: (areaId) => api.delete(`/business/areas/${areaId}`),
    // Delivery route: which area the round covers first, and which house within it.
    reorderAreas: (businessId, orderedIds) => api.put(`/business/${businessId}/areas/reorder`, { ordered_ids: orderedIds }),
    // Sends the two customers the moved one was dropped between; the server writes one row.
    moveCustomer: (businessId, customerId, prevId, nextId) =>
        api.put(`/business/${businessId}/customers/${customerId}/move`, { prev_id: prevId, next_id: nextId }),
    upsertCommunity: (data) => api.post('/business/communities/upsert', data),
    searchCommunities: (q) => api.get('/business/communities/search', { params: { q } }),

    // Customer management
    getCustomers: (businessId, includeArchived = false, statusFilter = null) => api.get(`/business/${businessId}/customers`, { params: { include_archived: includeArchived, status_filter: statusFilter } }),
    getCustomerTags: (businessId) => api.get(`/business/${businessId}/customer-tags`),
    createCustomer: (data) => api.post('/business/customers', data),
    updateCustomer: (customerId, data) => api.put(`/business/customers/${customerId}`, data),
    deleteCustomer: (customerId) => api.delete(`/business/customers/${customerId}`),
    
    // Customer subscription log management
    getCustomerSubscriptions: (businessId, customerId = null, productId = null, actionFilter = null, includeDeleted = false) => 
        api.get(`/business/${businessId}/customer-subscriptions`, { 
            params: { 
                customer_id: customerId, 
                product_id: productId, 
                action_filter: actionFilter, 
                include_deleted: includeDeleted 
            } 
        }),
    getCustomerSubscriptionDetails: (businessId, logId) => api.get(`/business/${businessId}/customer-subscriptions/${logId}`),
    createCustomerSubscription: (data) => api.post('/business/customer-subscriptions', data),
    updateCustomerSubscription: (logId, data) => api.put(`/business/customer-subscriptions/${logId}`, data),
    deleteCustomerSubscription: (logId) => api.delete(`/business/customer-subscriptions/${logId}`),
    getCustomerActiveSubscriptions: (customerId) => api.get(`/business/customers/${customerId}/active-subscriptions`),

    // Business member (agent) management. An agent is identified by their member row f_id —
    // not f_user_id — so they can be added before they have a Vendora account at all.
    // lookupUserByMobile is advisory: it answers { found: true|false } and never blocks an add.
    lookupUserByMobile: (mobile) => api.get('/business/users/lookup', { params: { mobile } }),
    getBusinessMembers: (businessId) => api.get(`/business/${businessId}/members`),
    addBusinessMember: (businessId, data) => api.post(`/business/${businessId}/members`, data),
    // Accepts any of { name, mobile, role }. Changing mobile re-points who the agent is.
    updateBusinessMember: (businessId, memberId, data) => api.put(`/business/${businessId}/members/${memberId}`, data),
    // moveCustomersTo: another member's id, or 'none' — required when the agent still has customers
    removeBusinessMember: (businessId, memberId, moveCustomersTo) => api.delete(`/business/${businessId}/members/${memberId}`, { params: moveCustomersTo != null ? { move_customers_to: moveCustomersTo } : {} }),

    // Customer notes
    getCustomerNotes: (customerId) => api.get(`/business/customers/${customerId}/notes`),
    addCustomerNote: (customerId, data) => api.post(`/business/customers/${customerId}/notes`, data),

    // Customer self-service (/my/ endpoints)
    getMyProducts: (businessId) => api.get(`/business/${businessId}/my/products`),
    getMyBusinessProfile: (businessId) => api.get(`/business/${businessId}/my/profile`),
    getMyOrders: (businessId, params = {}) => api.get(`/business/${businessId}/my/orders`, { params }),
    getMyInvoices: (businessId, params = {}) => api.get(`/business/${businessId}/my/invoices`, { params }),
    getMyPayments: (businessId, params = {}) => api.get(`/business/${businessId}/my/payments`, { params }),
    getMyBalance: (businessId) => api.get(`/business/${businessId}/my/balance`),
    getMySubscriptions: (businessId) => api.get(`/business/${businessId}/my/subscriptions`),
    // Customer self-service plan actions (pause/resume/change qty/stop) + undo a scheduled change
    customerSubscriptionAction: (businessId, data) => api.post(`/business/${businessId}/my/subscriptions/action`, data),
    cancelScheduledSubscriptionChange: (businessId, logId) => api.delete(`/business/${businessId}/my/subscriptions/scheduled/${logId}`),
    // Customer plan sign-up (request a NEW plan — vendor approves)
    getMyPlanProducts: (businessId) => api.get(`/business/${businessId}/my/plan-products`),
    requestPlanSignup: (businessId, data) => api.post(`/business/${businessId}/my/subscriptions/signup`, data),
    getMyPlanRequests: (businessId) => api.get(`/business/${businessId}/my/plan-requests`),

    // Community discovery
    getCommunityNearby: (lat, lng, radiusKm = 0.5) => api.get('/business/communities/nearby', { params: { lat, lng, radius_km: radiusKm } }),
    getCommunityVendors: (communityId) => api.get(`/business/communities/${communityId}/vendors`),
};

export const addressAPI = {
    list:       ()          => api.get('/business/users/me/addresses'),
    create:     (data)      => api.post('/business/users/me/addresses', data),
    update:     (id, data)  => api.put(`/business/users/me/addresses/${id}`, data),
    remove:     (id)        => api.delete(`/business/users/me/addresses/${id}`),
    setDefault: (id)        => api.patch(`/business/users/me/addresses/${id}/default`),
};

export const invoiceAPI = {
    // Invoice Period Management
    listInvoicePeriods: (businessId) => api.get(`/business/${businessId}/invoice-periods`),
    createInvoicePeriod: (businessId, data) => api.post(`/business/${businessId}/invoice-periods`, data),
    ensureWorkingPeriod: (businessId) => api.post(`/business/${businessId}/invoice-periods/ensure-working`),
    createEarlierPeriod: (businessId) => api.post(`/business/${businessId}/invoice-periods/create-earlier`),
    deleteEarliestPeriod: (businessId, periodId) =>
        api.delete(`/business/${businessId}/invoice-periods/${periodId}/delete-earliest`),
    generateSubscriptionTransactions: (businessId, periodId) => 
        api.post(`/business/${businessId}/invoice-periods/${periodId}/generate-subscription-txns`),
    getSubscriptionSummary: (businessId, periodId) =>
        api.get(`/business/${businessId}/invoice-periods/${periodId}/subscription-summary`),
    lockInvoicePeriod: (businessId, periodId) => 
        api.put(`/business/${businessId}/invoice-periods/${periodId}/lock`),
    updatePeriodNotes: (businessId, periodId, data) =>
        api.patch(`/business/${businessId}/invoice-periods/${periodId}/notes`, data),
    unlockInvoicePeriod: (businessId, periodId) => 
        api.put(`/business/${businessId}/invoice-periods/${periodId}/unlock`),
    checkPeriodStale: (businessId, periodId) =>
        api.get(`/business/${businessId}/invoice-periods/${periodId}/is-stale`),
    
    // Transaction Management
    listTransactions: (businessId, params = {}) => 
        api.get(`/business/${businessId}/transactions`, { params }),
    createTransaction: (businessId, data) => 
        api.post(`/business/${businessId}/transactions`, data),
    getTransaction: (businessId, transactionId) => 
        api.get(`/business/${businessId}/transactions/${transactionId}`),
    deleteTransaction: (businessId, transactionId) => 
        api.delete(`/business/${businessId}/transactions/${transactionId}`),
    
    // Invoice Management
    listInvoices: (businessId, params = {}) => 
        api.get(`/business/${businessId}/invoices`, { params }),
    generateInvoices: (businessId, data) => 
        api.post(`/business/${businessId}/invoices/generate`, data),
    getInvoiceDetails: (businessId, invoiceId) => 
        api.get(`/business/${businessId}/invoices/${invoiceId}`),
    addInvoiceAdjustment: (businessId, invoiceId, data) => 
        api.post(`/business/${businessId}/invoices/${invoiceId}/adjustments`, data),
    listInvoiceAdjustments: (businessId, invoiceId) => 
        api.get(`/business/${businessId}/invoices/${invoiceId}/adjustments`),
    deleteInvoiceAdjustment: (businessId, invoiceId, adjustmentId) => 
        api.delete(`/business/${businessId}/invoices/${invoiceId}/adjustments/${adjustmentId}`),
    getInvoiceShareUrl: (businessId, invoiceId, { preview = false } = {}) =>
        api.get(`/business/${businessId}/invoices/${invoiceId}/share-url`, { params: preview ? { preview: 1 } : {} }),
    getCustomerBalance: (businessId, customerId) => 
        api.get(`/business/${businessId}/invoices/customers/${customerId}/balance`),
    
    // Payment Management
    listPayments: (businessId, params = {}) => 
        api.get(`/business/${businessId}/payments`, { params }),
    createPayment: (businessId, data) => 
        api.post(`/business/${businessId}/payments`, data),
    recordPayment: (businessId, data) => 
        api.post(`/business/${businessId}/payments`, data),
    getPayment: (businessId, paymentId) => 
        api.get(`/business/${businessId}/payments/${paymentId}`),
    updatePayment: (businessId, paymentId, data) => 
        api.put(`/business/${businessId}/payments/${paymentId}`, data),
    deletePayment: (businessId, paymentId) => 
        api.delete(`/business/${businessId}/payments/${paymentId}`),
    markPaymentShared: (businessId, paymentId) =>
        api.post(`/business/${businessId}/payments/${paymentId}/mark-shared`),
    getPaymentShareUrl: (businessId, paymentId) =>
        api.get(`/business/${businessId}/payments/${paymentId}/share-url`),

    // Dues & Collections summary
    getDuesSummary: (businessId, params = {}) =>
        api.get(`/business/${businessId}/payments/dues-summary`, { params }),

    // Daily Ops (Absent / Extra deliveries)
    // showAll brings back plans that aren't scheduled to deliver today, so the vendor can still
    // record an off-schedule delivery.
    getDailyOps: (businessId, opsDate, areaId = null, agentId = null, showAll = false) =>
        api.get(`/business/${businessId}/transactions/daily-ops`, {
            params: {
                ops_date: opsDate,
                ...(areaId ? { area_id: areaId } : {}),
                ...(agentId ? { agent_id: agentId } : {}),
                ...(showAll ? { show_all: true } : {}),
            },
        }),
    // Renders the round the vendor is looking at as a PDF to share into WhatsApp. Rows are
    // sent from the client because the filters, sort and per-line quantity adjustments all
    // live here — the server would otherwise need a second copy of them.
    getDailyOpsPdf: (businessId, body) =>
        api.post(`/business/${businessId}/transactions/daily-ops/pdf`, body, {
            responseType: 'blob',
        }),
    recordDailyOpsEntry: (businessId, { customerId, productId, opsDate, origin, quantity = 1 }) =>
        api.post(`/business/${businessId}/transactions/daily-ops`, null, {
            params: {
                customer_id: customerId,
                product_id:  productId,
                ops_date:    opsDate,
                origin,
                quantity,
            },
        }),
    deleteDailyOpsEntry: (businessId, txnId) =>
        api.delete(`/business/${businessId}/transactions/${txnId}`),
    // Ticking a house off the round. Information only — it never affects the bill, unlike an
    // Absent entry. No row is stored for houses left unmarked.
    markDelivered: (businessId, customerId, opsDate) =>
        api.post(`/business/${businessId}/transactions/daily-ops/delivered`, null, {
            params: { customer_id: customerId, ops_date: opsDate },
        }),
    unmarkDelivered: (businessId, customerId, opsDate) =>
        api.delete(`/business/${businessId}/transactions/daily-ops/delivered`, {
            params: { customer_id: customerId, ops_date: opsDate },
        }),
};

export const adminAPI = {
    getDashboard: (deleted = false) => api.get('/admin/dashboard', { params: deleted ? { deleted: true } : {} }),
    getUsers: () => api.get('/admin/users'),
    getTableSizes: () => api.get('/admin/table-sizes'),
    joinBusiness: (businessId) => api.post(`/admin/businesses/${businessId}/join`),
    leaveBusiness: (businessId) => api.post(`/admin/businesses/${businessId}/leave`),
    deleteBusiness: (businessId) => api.delete(`/admin/businesses/${businessId}`),
    restoreBusiness: (businessId) => api.post(`/admin/businesses/${businessId}/restore`),
    // Communities
    getCommunities: (params) => api.get('/admin/communities', { params }),
    createCommunity: (data) => api.post('/admin/communities', data),
    updateCommunity: (id, data) => api.put(`/admin/communities/${id}`, data),
    deleteCommunity: (id) => api.delete(`/admin/communities/${id}`),
    // Area linking
    getBusinessAreas: () => api.get('/admin/business-areas'),
    linkAreaToCommunity: (areaId, communityId) => api.patch(`/admin/business-areas/${areaId}`, { f_community_id: communityId }),
    togglePublicStore: (businessId) => api.patch(`/admin/businesses/${businessId}/public-store`),
    // Master product categories
    getProductCategories: () => api.get('/admin/product-categories'),
    createProductCategory: (data) => api.post('/admin/product-categories', data),
    updateProductCategory: (id, data) => api.put(`/admin/product-categories/${id}`, data),
    deleteProductCategory: (id) => api.delete(`/admin/product-categories/${id}`),
    // Master products
    getMasterProducts: (categoryId) => api.get('/admin/products', { params: categoryId ? { category_id: categoryId } : {} }),
    createMasterProduct: (data) => api.post('/admin/products', data),
    updateMasterProduct: (id, data) => api.put(`/admin/products/${id}`, data),
    deleteMasterProduct: (id) => api.delete(`/admin/products/${id}`),
    // Master product images (S3 folder per product)
    getMasterProductImages: (id) => api.get(`/admin/products/${id}/images`),
    uploadMasterProductImage: (id, file) => {
        const form = new FormData();
        form.append('file', file);
        return api.post(`/admin/products/${id}/images`, form, { headers: { 'Content-Type': 'multipart/form-data' } });
    },
    deleteMasterProductImage: (id, key) => api.delete(`/admin/products/${id}/images`, { params: { key } }),
};

export const orderAPI = {
    listOrders: (businessId, params) => api.get(`/business/${businessId}/orders`, { params }),
    getOrdersSummary: (businessId, params) => api.get(`/business/${businessId}/orders/summary`, { params }),
    createOrder: (businessId, data) => api.post(`/business/${businessId}/orders`, data),
    getOrder: (businessId, orderId) => api.get(`/business/${businessId}/orders/${orderId}`),
    updateOrderStatus: (businessId, orderId, data) => api.patch(`/business/${businessId}/orders/${orderId}/status`, data),
    updateOrder: (businessId, orderId, data) => api.put(`/business/${businessId}/orders/${orderId}`, data),
    createCustomerOrder: (businessId, data) => api.post(`/business/${businessId}/orders/customer`, data),
    getProductDefaults: (businessId, productId) => api.get(`/business/${businessId}/orders/product-defaults/${productId}`),
    getOrderShareUrl: (businessId, orderId) => api.get(`/business/${businessId}/orders/${orderId}/share-url`),
    getPrepSummary:  (businessId) => api.get(`/business/${businessId}/orders/prep-summary`),
};

// Vendor-facing notifications (v1: customer payment confirmations)
export const notificationAPI = {
    // channel: 'dues' (bill+dues) for the Dues page, 'orders' for the Orders dashboard
    list:    (businessId, params) => api.get(`/business/${businessId}/notifications`, { params }),
    summary: (businessId) => api.get(`/business/${businessId}/notifications/summary`),
    accept:  (businessId, id) => api.post(`/business/${businessId}/notifications/${id}/accept`),
    reject:  (businessId, id) => api.post(`/business/${businessId}/notifications/${id}/reject`),
};

// Vendor-facing plan sign-up requests (customer wants a NEW plan; vendor approves/declines)
export const planSignupAPI = {
    list:    (businessId, params) => api.get(`/business/${businessId}/plan-signups`, { params }),
    approve: (businessId, id, data) => api.post(`/business/${businessId}/plan-signups/${id}/approve`, data),
    decline: (businessId, id) => api.post(`/business/${businessId}/plan-signups/${id}/decline`),
};

// Public store — no auth header needed
const publicApi = axios.create({
    baseURL: '',
    headers: { 'Content-Type': 'application/json' },
});

export const publicStoreAPI = {
    getCatalog:          (businessId) => publicApi.get(`/api/public/store/${businessId}`),
    placeOrder:          (businessId, data) => publicApi.post(`/api/public/store/${businessId}/order`, data),
    getPlanProducts:     (businessId) => publicApi.get(`/api/public/store/${businessId}/plan-products`),
    planSignup:          (businessId, data) => publicApi.post(`/api/public/store/${businessId}/plan-signup`, data),
    sendOtp:             (mobile) => publicApi.post('/api/auth/send-otp', { mobile, channel: 'whatsapp' }),
    verifyAndRegister:   (mobile, otp, password, name) =>
        publicApi.post('/api/auth/verify-otp-and-set-password', { mobile, otp }, { params: { password, ...(name ? { name } : {}) } }),
    getAuthUser:         (token) => publicApi.get('/api/auth/me', { headers: { Authorization: `Bearer ${token}` } }),
    getMyProfile:        (businessId, token) => publicApi.get(`/api/business/${businessId}/my/profile`, { headers: { Authorization: `Bearer ${token}` } }),
    // Guest community discovery — no auth needed
    getCommunitiesNearby: (lat, lng, radiusKm = 5.0) =>
        publicApi.get('/api/public/communities/nearby', { params: { lat, lng, radius_km: radiusKm } }),
    searchCommunities:   (q) => publicApi.get('/api/public/communities/search', { params: { q } }),
    getCommunityVendors: (communityId) => publicApi.get(`/api/public/communities/${communityId}/vendors`),
};

export const communityAPI = {
    nearby: (lat, lng, radiusKm = 5) =>
        publicApi.get('/api/public/communities/nearby', { params: { lat, lng, radius_km: radiusKm } }),
    search: (q) =>
        publicApi.get('/api/public/communities/search', { params: { q } }),
    fromLocation: (lat, lng) =>
        publicApi.get('/api/public/communities/from-location', { params: { lat, lng } }),
};

export default api;